import { Card } from "~/features/configurator/Linear/components/Card";
import { type ReactNode } from "react";
import { Collapse, IconButton, styled, Typography } from "~/common/mui";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const Root = styled(Card, { shouldForwardProp: (p) => p !== "error" })<{ error?: boolean }>(
  ({ theme, error }) => ({
    padding: theme.spacing(2),
    ...(error && {
      "&::after": {
        borderColor: `${theme.palette.error.main} !important` as string,
      },
    }),
  }),
);

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.up("tablet")]: {
    justifyContent: "space-between",
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.titleMedium,
  marginTop: 0,
  marginBottom: theme.spacing(0.5),
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  marginLeft: "auto !important",
  [theme.breakpoints.up("tablet")]: {
    margin: 0,
  },
}));

interface InformationCardProps {
  className?: string;
  children?: ReactNode;
  title: string;
  editAction?: () => void;
  error?: boolean;
}

const InformationCard = ({ title, editAction, error, className, children }: InformationCardProps) => {
  const { val: isOpen, setTrue: openCard, setFalse: closeCard } = useBooleanState(true);

  const isTablet = useIsMinTabletSize();
  const ToggleIcon = isOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;

  return (
    <Root disableHover error={error} className={className}>
      <Header>
        <HeaderTitle variant="titleMedium" as="h5">
          {title}
        </HeaderTitle>

        {editAction && (
          <IconButton onClick={editAction} color={"primary"}>
            <EditIcon  />
          </IconButton>
        )}
        {!isTablet && (
          <ToggleButton onClick={isOpen ? closeCard : openCard}>
            <ToggleIcon  />
          </ToggleButton>
        )}
      </Header>

      <Collapse in={isOpen}>{children}</Collapse>
    </Root>
  );
};

export default InformationCard;
